<template>
  <div class="letter-numbers-step">
    <div class="letter-numbers-step__title" @click="handleToggle">
      <div class="letter-numbers-step__label">
        Step {{ step }}: <span class="letter-numbers-step__name">{{ label }}</span>
        <slot v-if="isOpened" name="action"></slot>
      </div>

      <UseSvg
        :class="{
          'letter-numbers-step__icon': true,
          'letter-numbers-step__icon--opened': isOpened
        }"
        icon="chevron-down"
      />
    </div>

    <div v-show="isOpened" class="letter-numbers-step__content">
      <slot />
    </div>
  </div>
</template>

<script setup>
import { ref, watch, computed, onMounted } from "vue";
import UseSvg from "common/vue/components/UseSvg.vue";
import { useDtfStore } from "./store";

const props = defineProps({
  label: {
    type: String,
    required: true
  },
  step: {
    type: Number,
    default: null
  },
  initiallyOpen: {
    type: Boolean,
    default: false
  }
});

const store = useDtfStore();
const isLetters = computed(() => store.isLetters);
const letterNumberArtwork = computed(() => store.letterNumberArtwork);
const letterHeightAndMaxLength = computed(() => {
  const { height, maxLength } = letterNumberArtwork.value;

  return Boolean(height && maxLength);
});

const loadFromConfig = computed(() => store.loadFromConfig);
const isOpened = ref(props.initiallyOpen);

const handleToggle = () => {
  isOpened.value = !isOpened.value;
};

const watchSection = (property, steps) => {
  watch(
    () => letterNumberArtwork.value[property],
    (newValue, oldValue) => {
      if (steps.includes(props.step)) {
        if (!oldValue && newValue) {
          isOpened.value = true;
        }
        // reverted to null
        if (!props.initiallyOpen) {
          if (oldValue && newValue === null && !props.initiallyOpen) {
            isOpened.value = false;
          }
        }
      }
    }
  );
};

const watchSizesLetterSection = steps => {
  watch(
    () => letterHeightAndMaxLength.value,
    (newValue, oldValue) => {
      if (steps.includes(props.step)) {
        if (!oldValue && newValue) {
          isOpened.value = true;
        }
      }
    }
  );

  // collapse 'Step 4: Select Color'
  watch(
    () => letterNumberArtwork.value,
    (newArtwork, _oldArtwork) => {
      if (steps.includes(props.step)) {
        if (
          newArtwork.height === null &&
          newArtwork.maxLength === null &&
          newArtwork.color === null
        ) {
          isOpened.value = false;
        }
      }
    }
  );
};

// Watchers for each section transition
// common to Jersey Numbers & Letters
watchSection("style", [2]); // open 'Step 2: Select Height (Numbers) or Font (Letters)'

if (isLetters.value) {
  watchSection("font", [3]); // open 'Step 3: Select Size'
  watchSizesLetterSection([4]); // open 'Step 4: Select Color'
  watchSection("color", [5]); // open 'Step 5: Add Name/Word'
} else {
  // Jersey Numbers 'Select Color' is 'Step 3'
  watchSection("height", [3]);
  watchSection("color", [4, 5]); // open 'Step 4: Preview' and 'Step 5: Select Quantity'
}

onMounted(() => {
  if (!loadFromConfig.value) {
    isOpened.value = true;
  }
});
</script>

<style lang="scss">
@import "frontend/init/jiffy/settings.scss";
@import "frontend/init/jiffy/mixin.scss";

.letter-numbers-step {
  padding: 12px 12px 16px;
  border-radius: 8px;
  border: 1px solid $gray-border;
  background: $white;
  box-shadow: 0 25px 30px 0 rgba($black, 0.05);

  @include with-tablet {
    padding: 16px;
  }

  &__title {
    display: flex;
    justify-content: space-between;
    align-items: center;
    cursor: pointer;
  }

  &__label {
    color: $text-black;
    font-size: 16px;
    font-weight: 700;
    line-height: 1.3;
  }

  &__name {
    color: $warm-grey;
  }

  &__icon {
    width: 12px;
    height: 12px;

    &--opened {
      transform: rotate(180deg);
    }
  }

  &__content {
    margin-top: 12px;

    @include with-tablet {
      margin-top: 16px;
    }
  }
}
</style>
